import React from "react";

/**
 * Schedule Details Modal Component
 */
const ScheduleDetailsModal = ({
  showInfoWindow,
  selectedSchedule,
  closeInfoWindow,
  t,
  getStatusColor,
  getStatusText,
  copyLocationLink,
  copyButtonText,
  isCopying,
}) => {
  if (!showInfoWindow || !selectedSchedule) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={closeInfoWindow}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">
              {selectedSchedule.subscriptionName}
            </h3>
            <p className="text-sm text-gray-500">
              {t("scheduleMap.deliveryDetails")}
            </p>
          </div>
          <button
            onClick={closeInfoWindow}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content - Two Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          {/* Left Column - Delivery Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {t("scheduleMap.deliveryInformation")}
            </h4>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">
                  {t("scheduleMap.status")}:
                </span>
                <span
                  className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: `${getStatusColor(
                      selectedSchedule.status
                    )}20`,
                    color: getStatusColor(selectedSchedule.status),
                  }}
                >
                  {getStatusText(selectedSchedule.status)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">
                  {t("scheduleMap.deliveryTime")}:
                </span>
                <span className="text-gray-900">
                  {selectedSchedule.timeToServeFrom &&
                  selectedSchedule.timeToServeTo
                    ? `${selectedSchedule.timeToServeFrom}:00 - ${selectedSchedule.timeToServeTo}:00`
                    : t("scheduleMap.noTimeSpecified")}
                </span>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700 block mb-2">
                  {t("scheduleMap.meals")}:
                </span>
                <div className="text-gray-900">
                  {selectedSchedule.meals &&
                  selectedSchedule.meals.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {selectedSchedule.meals.map((meal, index) => (
                        <li key={index} className="text-sm">
                          {meal.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-gray-500 italic">
                      {t("scheduleMap.noMeals")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Location Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {t("scheduleMap.locationDetails")}
            </h4>

            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-gray-700 block mb-2">
                  {t("scheduleMap.location")}:
                </span>
                <div className="space-y-2">
                  <div className="text-gray-900 font-medium">
                    {selectedSchedule.location.name}
                  </div>

                  {selectedSchedule.location.buildingNumber && (
                    <div className="text-sm text-gray-600 flex items-center">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      {t("scheduleMap.building")}:{" "}
                      {selectedSchedule.location.buildingNumber}
                    </div>
                  )}

                  {selectedSchedule.location.apartmentNumber && (
                    <div className="text-sm text-gray-600 flex items-center">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                        />
                      </svg>
                      {t("scheduleMap.apartment")}:{" "}
                      {selectedSchedule.location.apartmentNumber}
                    </div>
                  )}

                  {selectedSchedule.location.phoneNumber && (
                    <div className="text-sm text-gray-600 flex items-center">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      {t("scheduleMap.phone")}:{" "}
                      {selectedSchedule.location.phoneNumber}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Copy Location Link Button */}
        <div className="pt-6 border-t border-gray-200">
          <button
            onClick={copyLocationLink}
            disabled={isCopying}
            className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <svg
              className="-ml-1 mr-3 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            {copyButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDetailsModal;
